import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Loader2, ArrowLeft, TrendingUp, DollarSign, Receipt, Target, Calendar, AlertTriangle, Bell, Check, Building2, Circle, Info } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { IFRiskTrendChart } from '../components/IFRiskTrendChart';
import { useIFRiskHistorico } from '../hooks/useIFRiskHistorico';
import { useIFAlertas, useIFMarcarAlertaLido, type IFAlerta } from '../hooks/useIFAlertas';
import { useIFExecucoesNaoMedidasContrato } from '../hooks/useIFExecucoesNaoMedidas';
import { useIFBoletinsPorStatusContrato } from '../hooks/useIFBoletinsPorStatus';
import { ExecucoesNaoMedidasCard, BoletinsPorStatusCard, fmtBRLShort } from '../components/IFFiduciaryCards';
import { IFConfidenceScore } from '../components/IFConfidenceScore';
import { IFIntegritySeal } from '../components/IFIntegritySeal';
import { EmpMedicoesView } from '@/components/empreendimentos/EmpMedicoesView';
import { EmpExecucaoView } from '@/components/empreendimentos/EmpExecucaoView';
import { EmpRiskIntelligenceView } from '@/components/empreendimentos/EmpRiskIntelligenceView';
import { EmpIndicadoresView } from '@/components/empreendimentos/EmpIndicadoresView';
import { PanoramaProgressoChart } from '@/components/empreendimentos/PanoramaProgressoChart';
import { SectionCard } from '@/components/system/SectionCard';
import { useEmpMonteCarloLatest } from '@/hooks/useEmpMonteCarlo';
import { PredictiveAlertsBanner } from '@/components/empreendimentos/PredictiveAlertsBanner';
import { FinancierExportButton } from '@/components/empreendimentos/FinancierExportButton';
import { useEmpAlertasPreditivos } from '@/hooks/useEmpAlertasPreditivos';
import { useEmpRelatorioFinanciador } from '@/hooks/useEmpRelatorioFinanciador';
import { useIntegridadeFinanceira } from '@/hooks/useIntegridadeFinanceira';
import { useEmpCronogramaVersoes } from '@/hooks/useEmpCronograma';

/* ── Status badge config ─────────────────────────────────────────── */
const STATUS_VISUAL: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  ativo: { label: 'Ativo', variant: 'default' },
  concluido: { label: 'Concluído', variant: 'secondary' },
  suspenso: { label: 'Suspenso', variant: 'destructive' },
  cancelado: { label: 'Cancelado', variant: 'destructive' },
};

/* ── Alert severity helpers ──────────────────────────────────────── */
function alertSeverityIcon(tipo: string) {
  switch (tipo) {
    case 'risco_elevado':
    case 'prazo_critico':
      return <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />;
    case 'desvio_progresso':
      return <AlertTriangle className="h-4 w-4 text-warning shrink-0" />;
    default:
      return <Bell className="h-4 w-4 text-muted-foreground shrink-0" />;
  }
}

function alertSeverityBg(tipo: string) {
  switch (tipo) {
    case 'risco_elevado':
    case 'prazo_critico':
      return 'bg-destructive/5 hover:bg-destructive/10 border-l-2 border-destructive/30';
    case 'desvio_progresso':
      return 'bg-warning/5 hover:bg-warning/10 border-l-2 border-warning/30';
    default:
      return 'bg-muted/30 hover:bg-muted/50';
  }
}

/* ── Health Semaphore ────────────────────────────────────────────── */
type HealthLevel = 'healthy' | 'warning' | 'critical' | 'neutral';

function computeHealthLevel(spi: number | null, cpi: number | null, probAtraso: number | null): HealthLevel {
  if (spi === null && cpi === null) return 'neutral';
  const s = spi ?? 1;
  const c = cpi ?? 1;
  const delay = probAtraso ?? 0;
  if (s < 0.85 || c < 0.85 || delay > 60) return 'critical';
  if (s < 1.0 || c < 1.0) return 'warning';
  return 'healthy';
}

const HEALTH_CONFIG: Record<HealthLevel, { color: string; bg: string; label: string; tooltip: string }> = {
  healthy:  { color: 'text-success',     bg: 'bg-success/15',     label: 'Saudável',  tooltip: 'SPI e CPI ≥ 1.0 — contrato dentro do esperado' },
  warning:  { color: 'text-warning',     bg: 'bg-warning/15',     label: 'Atenção',   tooltip: 'SPI ou CPI entre 0.85 e 0.99 — monitorar de perto' },
  critical: { color: 'text-destructive', bg: 'bg-destructive/15', label: 'Crítico',   tooltip: 'SPI ou CPI < 0.85 ou probabilidade de atraso > 60%' },
  neutral:  { color: 'text-muted-foreground', bg: 'bg-muted/50',  label: 'Sem EVM',   tooltip: 'Dados de EVM ainda não disponíveis para este contrato' },
};

function HealthSemaphore({ level }: { level: HealthLevel }) {
  const cfg = HEALTH_CONFIG[level];
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${cfg.bg} shrink-0 cursor-default`}>
            <Circle className={`h-2.5 w-2.5 fill-current ${cfg.color}`} />
            <span className={`text-[10px] font-bold ${cfg.color}`}>{cfg.label}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom"><p>{cfg.tooltip}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────── */
function spiCpiColor(v: number): string {
  if (v >= 1) return 'text-success';
  if (v >= 0.9) return 'text-warning';
  return 'text-destructive';
}

function spiCpiBg(v: number): string {
  if (v >= 1) return 'bg-success/8';
  if (v >= 0.9) return 'bg-warning/8';
  return 'bg-destructive/8';
}

/* ── KPI Metric ──────────────────────────────────────────────────── */
function KpiMetric({ label, value, sub, icon: Icon, colorClass, bgClass }: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
  colorClass?: string;
  bgClass?: string;
}) {
  return (
    <div className="flex items-start gap-3 py-3">
      <div className={`flex items-center justify-center h-8 w-8 rounded-lg shrink-0 ${bgClass || 'bg-muted/50'}`}>
        <Icon className={`h-4 w-4 ${colorClass || 'text-muted-foreground'}`} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider leading-none mb-1">{label}</p>
        <p className={`text-lg font-bold tabular-nums leading-none font-display ${colorClass || 'text-foreground'}`}>
          {value}
        </p>
        {sub && <p className="text-[10px] text-muted-foreground/60 mt-0.5 leading-tight">{sub}</p>}
      </div>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────── */
const IFContratoDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  /* Data hooks (unchanged) */
  const { data: contrato, isLoading: loadC } = useQuery({
    queryKey: ['if-contrato', id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('emp_contratos')
        .select('id, nome, contratada, valor_global, data_inicio, data_fim_prevista, status')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: progresso } = useQuery({
    queryKey: ['if-prog', id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('emp_vw_progresso_fisico_contrato')
        .select('*').eq('contrato_id', id).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: riskHistory = [], isLoading: loadRisk } = useIFRiskHistorico(id || null);
  const { data: alertas = [] } = useIFAlertas(id || undefined);
  const marcarLido = useIFMarcarAlertaLido();
  const { data: mcLatest } = useEmpMonteCarloLatest(id || null);
  const { data: predAlertas = [] } = useEmpAlertasPreditivos(id || null);
  const { data: relFinanciador } = useEmpRelatorioFinanciador(id || null);
  const { data: execNaoMedida, isLoading: loadExec } = useIFExecucoesNaoMedidasContrato(id || null);
  const { data: boletinsStatus, isLoading: loadBol } = useIFBoletinsPorStatusContrato(id || null);

  // Financial integrity — find active schedule version
  const { data: cronVersoes = [] } = useEmpCronogramaVersoes(id || '');
  const versaoVigente = cronVersoes.find(v => v.status === 'vigente') || cronVersoes[0] || null;
  const integridade = useIntegridadeFinanceira(versaoVigente?.id || null, id || undefined);

  const { data: evmContrato } = useQuery({
    queryKey: ['if-evm', id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('emp_vw_evm_contrato')
        .select('contrato_id, bac, ev, ac, spi, cpi, eac')
        .eq('contrato_id', id)
        .order('data', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.debug('[EVM:IF] EVM query failed, cards hidden:', error.message);
        return null;
      }
      return data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  /* Loading */
  if (loadC || !contrato) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  /* Derived values */
  const pct = Math.min(100, Number(progresso?.progresso_fisico_pct) || 0);
  const diasRestantes = contrato.data_fim_prevista
    ? Math.max(0, Math.ceil((new Date(contrato.data_fim_prevista).getTime() - Date.now()) / 86400000))
    : null;
  const statusVisual = STATUS_VISUAL[contrato.status] || { label: contrato.status || '—', variant: 'outline' as const };

  const spi = evmContrato?.spi != null ? Number(evmContrato.spi) : null;
  const cpi = evmContrato?.cpi != null ? Number(evmContrato.cpi) : null;
  const eac = evmContrato?.eac != null ? Number(evmContrato.eac) : null;

  return (
    <div className="pb-12 max-w-[1400px] mx-auto">

      {/* ═══════════════════════════════════════════════════════════════
          HEADER — Clean identity bar
          ═══════════════════════════════════════════════════════════════ */}
      <header className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 -ml-2 h-8 w-8"
            onClick={() => navigate('/financiamento/painel')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-lg font-bold text-foreground truncate leading-tight font-display tracking-tight">
                {contrato.nome}
              </h1>
              <Badge variant={statusVisual.variant} className="shrink-0 text-[10px] font-semibold">
                {statusVisual.label}
              </Badge>
              <HealthSemaphore level={computeHealthLevel(spi, cpi, mcLatest ? (100 - mcLatest.prob_on_time) : null)} />
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Building2 className="h-3 w-3 text-muted-foreground/50" />
              <p className="text-xs text-muted-foreground/70 truncate">{contrato.contratada}</p>
            </div>
          </div>
        </div>
        <FinancierExportButton
          contratoId={contrato.id}
          contratoNome={contrato.nome}
          data={relFinanciador ?? null}
        />
      </header>

      {/* ═══════════════════════════════════════════════════════════════
          BLOCO 1 — HERO: Confiança + KPIs | Integridade
          Two-column layout in a single unified card
          ═══════════════════════════════════════════════════════════════ */}
      <section className="emp-card p-0 overflow-hidden mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          {/* ── Left: Score + KPIs (8 cols) ─────────────────────── */}
          <div className="lg:col-span-8 p-6 lg:p-8">
            <IFConfidenceScore contratoId={contrato.id} />

            {/* KPI Strip — below the score */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 mt-6 pt-5 border-t border-border/30" style={{ minHeight: '160px' }}>
            <KpiMetric
                label="Progresso Geral"
                value={`${pct.toFixed(1)}%`}
                icon={Target}
                colorClass={pct >= 80 ? 'text-success' : pct >= 50 ? 'text-foreground' : 'text-warning'}
                bgClass={pct >= 80 ? 'bg-success/8' : pct >= 50 ? 'bg-muted/50' : 'bg-warning/8'}
              />
              <KpiMetric
                label="SPI"
                value={spi !== null ? spi.toFixed(2) : '—'}
                sub={spi !== null ? 'Aderência ao prazo' : 'Sem dados EVM'}
                icon={TrendingUp}
                colorClass={spi !== null ? spiCpiColor(spi) : 'text-muted-foreground'}
                bgClass={spi !== null ? spiCpiBg(spi) : 'bg-muted/50'}
              />
              <KpiMetric
                label="CPI"
                value={cpi !== null ? cpi.toFixed(2) : '—'}
                sub={cpi !== null ? 'Eficiência de custo' : 'Sem dados EVM'}
                icon={DollarSign}
                colorClass={cpi !== null ? spiCpiColor(cpi) : 'text-muted-foreground'}
                bgClass={cpi !== null ? spiCpiBg(cpi) : 'bg-muted/50'}
              />
              <KpiMetric
                label="Prazo"
                value={diasRestantes !== null ? `${diasRestantes}d` : '—'}
                sub={diasRestantes !== null && diasRestantes < 60 ? 'Atenção ao prazo' : undefined}
                icon={Calendar}
                colorClass={diasRestantes !== null && diasRestantes < 30 ? 'text-destructive' : undefined}
                bgClass={diasRestantes !== null && diasRestantes < 30 ? 'bg-destructive/8' : 'bg-muted/50'}
              />
              <KpiMetric
                label="EAC"
                value={eac !== null ? `R$ ${(eac / 1_000_000).toFixed(1)}M` : '—'}
                sub={eac !== null ? 'Estimativa no término' : 'Sem dados EVM'}
                icon={Receipt}
                bgClass="bg-muted/50"
                colorClass={eac === null ? 'text-muted-foreground' : undefined}
              />
              <KpiMetric
                label="Prob. Prazo"
                value={mcLatest ? `${mcLatest.prob_on_time.toFixed(0)}%` : '—'}
                sub={mcLatest ? 'Monte Carlo' : 'Sem simulação'}
                icon={Target}
                colorClass={mcLatest ? (mcLatest.prob_on_time >= 70 ? 'text-success' : mcLatest.prob_on_time >= 40 ? 'text-warning' : 'text-destructive') : 'text-muted-foreground'}
                bgClass={mcLatest ? (mcLatest.prob_on_time >= 70 ? 'bg-success/8' : mcLatest.prob_on_time >= 40 ? 'bg-warning/8' : 'bg-destructive/8') : 'bg-muted/50'}
              />
            </div>
          </div>

          {/* ── Right: Integrity Seal (4 cols) ──────────────────── */}
          <div className="lg:col-span-4 bg-muted/20 border-t lg:border-t-0 lg:border-l border-border/30 p-6 lg:p-8 flex flex-col">
            <IFIntegritySeal contratoId={contrato.id} />

            {/* Fiduciary summary — compact, below seal */}
            <div className="mt-auto pt-6 space-y-5">
              {/* Financial integrity block */}
              {integridade.hasData && (
                <div className="border-t border-border/30 pt-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Integridade Orçamentária</p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground/50 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[200px]">
                          <p className="text-xs">Valores não vinculados distorcem o BAC e o risco</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-xs">Cobertura</span>
                      <span className={`font-bold tabular-nums text-xs ${
                        integridade.status === 'critical' ? 'text-destructive' :
                        integridade.status === 'warning' ? 'text-warning' : 'text-success'
                      }`}>
                        {integridade.status === 'critical' ? '🔴' : integridade.status === 'warning' ? '🟡' : '🟢'}{' '}
                        {integridade.coberturaFinanceira.toFixed(0)}%
                      </span>
                    </div>
                    {integridade.valorNaoVinculado > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground text-xs">Não vinculado</span>
                        <span className="font-medium tabular-nums text-xs text-destructive">
                          {fmtBRLShort(integridade.valorNaoVinculado)}
                        </span>
                      </div>
                    )}
                    {integridade.divergenciaBac > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground text-xs">Divergência BAC</span>
                        <span className={`font-medium tabular-nums text-xs ${integridade.divergenciaBac > 2 ? 'text-destructive' : 'text-muted-foreground'}`}>
                          {integridade.divergenciaBac.toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t border-border/30 pt-4">
                <ExecucoesNaoMedidasCard data={null} single={execNaoMedida} isLoading={loadExec} embedded />
              </div>
              <div>
                <BoletinsPorStatusCard resumos={boletinsStatus} isLoading={loadBol} embedded />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          BLOCO 2 — CURVA S (full width, generous height)
          ═══════════════════════════════════════════════════════════════ */}
      <section className="mb-8">
        <PanoramaProgressoChart
          contratoId={contrato.id}
          dataFimPrevista={contrato.data_fim_prevista}
          mcLatest={mcLatest}
          relFin={relFinanciador ?? null}
        />
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          BLOCO 3 — ALERTAS + RISCO (balanced 2 columns)
          ═══════════════════════════════════════════════════════════════ */}
      {(predAlertas.length > 0 || (alertas as IFAlerta[]).length > 0 || riskHistory.length >= 2) && (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Left: Alerts */}
          <div className="flex flex-col gap-4">
            {predAlertas.length > 0 && <PredictiveAlertsBanner alertas={predAlertas} />}
            {(alertas as IFAlerta[]).length > 0 && (
              <SectionCard title="Alertas Recentes" icon={Bell}>
                <div className="space-y-2">
                  {(alertas as IFAlerta[]).slice(0, 5).map((a) => (
                    <div key={a.id} className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${alertSeverityBg(a.tipo)}`}>
                       {alertSeverityIcon(a.tipo)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{a.titulo}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {formatDistanceToNow(new Date(a.created_at), { addSuffix: true, locale: ptBR })}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        disabled={marcarLido.isPending}
                        onClick={() => marcarLido.mutate(a.id)}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}
          </div>

          {/* Right: Risk Trend */}
          <div>
            {riskHistory.length >= 2 && (
              <IFRiskTrendChart data={riskHistory} isLoading={loadRisk} />
            )}
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          BLOCO 4 — ABAS OPERACIONAIS (READ-ONLY)
          ═══════════════════════════════════════════════════════════════ */}
      <section>
        <Tabs defaultValue="execucao" className="space-y-4">
          <TabsList>
            <TabsTrigger value="execucao">Execução</TabsTrigger>
            <TabsTrigger value="medicoes">Medições</TabsTrigger>
            <TabsTrigger value="indicadores">Indicadores</TabsTrigger>
            <TabsTrigger value="risco">Inteligência de Risco</TabsTrigger>
          </TabsList>
          <TabsContent value="execucao" className="mt-0">
            <EmpExecucaoView contratoId={contrato.id} contratoNome={contrato.nome} canEdit={false} />
          </TabsContent>
          <TabsContent value="medicoes" className="mt-0">
            <EmpMedicoesView contratoId={contrato.id} contratoNome={contrato.nome} canEdit={false} />
          </TabsContent>
          <TabsContent value="indicadores" className="mt-0">
            <EmpIndicadoresView contratoId={contrato.id} contratoNome={contrato.nome} canEdit={false} />
          </TabsContent>
          <TabsContent value="risco" className="mt-0">
            <EmpRiskIntelligenceView contratoId={contrato.id} contratoNome={contrato.nome} canEdit={false} />
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
};

export default IFContratoDetailPage;
